import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { fallanalyseAPI } from '@/lib/api';
import {
  CheckCircle2, AlertTriangle, Clock, ArrowRight,
  ChevronDown, ChevronUp, CircleDot, Loader2,
} from 'lucide-react';

const TYP_CONFIG = {
  klaerung: { icon: AlertTriangle, farbe: 'text-amber-600', bg: 'bg-amber-50/50 border-amber-200' },
  aktion: { icon: ArrowRight, farbe: 'text-emerald-600', bg: 'bg-emerald-50/50 border-emerald-200' },
  entscheidung: { icon: CircleDot, farbe: 'text-blue-600', bg: 'bg-blue-50/50 border-blue-200' },
  warten: { icon: Clock, farbe: 'text-slate-500', bg: 'bg-slate-50/50 border-slate-200' },
};

const REIFE_CONFIG = {
  bereit: { icon: CheckCircle2, farbe: 'text-emerald-600', bg: 'border-emerald-200 bg-emerald-50/40' },
  teilweise: { icon: AlertTriangle, farbe: 'text-amber-600', bg: 'border-amber-200 bg-amber-50/40' },
  noch_nicht: { icon: Clock, farbe: 'text-slate-500', bg: 'border-slate-300 bg-slate-50/40' },
};

export function AnschlussoptionenView({ entwurfId, stufeCStatus, lbTyp, t }) {
  const [open, setOpen] = useState(false);
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(false);

  const istVerfuegbar = stufeCStatus === 'freigeschaltet' && lbTyp === 'ki_lagebeurteilung';

  const ladeDaten = useCallback(async () => {
    if (!istVerfuegbar || daten) return;
    setLaden(true);
    try {
      const res = await fallanalyseAPI.entwurfAnschlussoptionen(entwurfId);
      setDaten(res.data);
    } catch {
      toast.error('Die Anschlussoptionen konnten nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }, [entwurfId, istVerfuegbar, daten]);

  useEffect(() => {
    if (open && !daten && istVerfuegbar) {
      ladeDaten();
    }
  }, [open, ladeDaten, daten, istVerfuegbar]);

  if (!istVerfuegbar) return null;

  const reife = daten ? REIFE_CONFIG[daten.entscheidungsreife] || REIFE_CONFIG.teilweise : null;
  const ReifeIcon = reife?.icon || Clock;

  return (
    <div className="mt-4" data-testid="anschlussoptionen-bereich">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors w-full text-left"
        data-testid="anschlussoptionen-toggle"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        <Compass className="h-4 w-4 text-primary" />
        {t('stufeD.titel')}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid="anschlussoptionen-inhalt">
          {laden ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('stufeD.laden')}
            </div>
          ) : daten ? (
            <>
              {/* Entscheidungsreife */}
              <div className={`flex items-start gap-2.5 p-3 rounded-lg border ${reife.bg}`} data-testid="entscheidungsreife">
                <ReifeIcon className={`h-4 w-4 mt-0.5 shrink-0 ${reife.farbe}`} />
                <div>
                  <p className="text-sm font-medium text-foreground">{t(`stufeD.reife_${daten.entscheidungsreife}`)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{daten.entscheidungsreife_text}</p>
                </div>
              </div>

              {/* Handlungsfelder */}
              {daten.handlungsfelder?.length > 0 && (
                <div className="space-y-2" data-testid="handlungsfelder-liste">
                  {daten.handlungsfelder.map((hf, i) => {
                    const cfg = TYP_CONFIG[hf.typ] || TYP_CONFIG.klaerung;
                    const Icon = cfg.icon;
                    return (
                      <div
                        key={i}
                        className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${cfg.bg}`}
                        data-testid={`handlungsfeld-${i}`}
                      >
                        <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${cfg.farbe}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground leading-relaxed">{hf.titel}</p>
                          {hf.hinweis && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">{hf.hinweis}</p>
                          )}
                        </div>
                        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/60 shrink-0">
                          {t(`stufeD.typ_${hf.typ}`)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Nächster konkreter Schritt (hervorgehoben) */}
              {daten.naechster_konkreter_schritt && (
                <div className="p-3 rounded-lg border border-primary/20 bg-primary/[0.03]" data-testid="naechster-schritt">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">
                    {t('stufeD.naechsterSchritt')}
                  </p>
                  <p className="text-sm text-foreground leading-relaxed">{daten.naechster_konkreter_schritt}</p>
                </div>
              )}

              <p className="text-[11px] text-muted-foreground/50 leading-relaxed">
                {t('stufeD.disclaimer')}
              </p>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// Re-export Compass so DashboardPage doesn't need a separate import
function Compass(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}
